import React, { useEffect, useState } from 'react';
import { FiChevronLeft, FiMail, FiPhone } from 'react-icons/fi';
import { useSession } from 'src/app/hooks/useSession';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import BgAluno from 'src/app/images/bg_aluno.jpg';
import TabNavBar from './TabNavBar';
import UserMetricsView from './UserMetricsView';
import UserMedalsView from './UserMedalsView';
import { useTranslation } from 'react-i18next';
import { usePlatformAbilities } from 'src/app/hooks/usePlatformAbilities';
import Onboarding from 'src/app/components/Onboarding';
import { DashboardContent } from '@/layouts/dashboard';
import { ProfileBanner, ProfileBannerBg, ProfileBannerBgWrapper, ProfileBannerData, ProfileBannerHeader, ProfileBannerInfo, ProfileBannerInfoWrapper, ProfileBannerInner, ProfileBannerTitle } from '@/components/custom/profile';
import { Filter, FilterContent } from '@/components/custom/filter';
import { Button } from '@/components/ui/button';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

export function moreThanThirtyDays(last_access) {
  const currentDate = new Date();
  const lastAccessDate = new Date(last_access);
  const difference = (currentDate - lastAccessDate) / (1000 * 3600 * 24);
  return difference < 30;
}

export function ShowUserAdmin(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { path, uri } = props;
  const { session } = useSession();
  const client = useClient();
  const location = useLocation();

  const profiles = session.user?.profileNames || (session.user && session.user['profile-names']) || [];
  const { id } = useParams();
  const { selectedTab } = useParams();
  const isCurrentUser = id == session.user.id;
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStudent, setIsStudent] = useState();
  const [userMetrics, setUserMetrics] = useState();
  const { abilities } = usePlatformAbilities();
  const [medals, setMedals] = useState([]);
  const [medalsLoading, setMedalsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    getUser();
  }, [id]);

  const lastAccess = user?.['last-access'];
  const isActive = moreThanThirtyDays(lastAccess);

  const getUser = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`users/${id}`);
    if (error) {
      toast.error(t('toast.errorGetStudentInfo'));
    } else {
      setUser(data);
      const newIsStudent = data?.['profile-names']?.[0] === 'Aluno';
      setIsStudent(newIsStudent);
      const isAdmin = !profiles?.includes('Aluno');
      setIsAdmin(isAdmin);
      getUserMetrics();
    }
    setLoading(false);
  };

  const getUserMetrics = async () => {
    const { data, error } = await client.fetch(`general_metrics?filter[user_id]=${id}&learning_system_users=true`);
    if (error) {
      toast.error(t('toast.errorGetStudentMetrics'));
    } else {
      setUserMetrics(data);
    }
  };

  const getMedals = async () => {
    setMedalsLoading(true);
    const { data, error } = await client.fetch('medals');
    if (error) {
      toast.error(t('toast.errorMedals'));
    } else {
      setMedals(data);
      setMedalsLoading(false);
    }
  };

  const isOwner = () => {
    if (session?.user?.id === user?.id || isAdmin) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    getMedals();
  }, []);

  const userPhone = user?.['user-detail']?.phone;

  const breadCrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: uri,
        name: 'Desempenho',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent breadcrumbs={breadCrumbs}>
      <ProfileBanner>
        {location?.state?.isRedirected && (
          <Filter>
            <FilterContent>
              <button
                className="filter-bar__back"
                onClick={() => navigate(-1)}
              >
                <span className="card__icon-wrapper">
                  <FiChevronLeft className="card__icon" />
                </span>
                <span className="filter-bar__back-container">
                  <span className="filter-bar__title">Voltar</span>
                </span>
              </button>
            </FilterContent>
          </Filter>
        )}

        <Button
          variant="outline"
          className="absolute right-3 top-3 z-10"
          wide
          asChild
        >
          <Link to={`/course/edit/participant/${id}`}>{t('button.edit')}</Link>
        </Button>

        <ProfileBannerBgWrapper>
          <ProfileBannerBg
            src={BgAluno}
            alt="imagem de fundo"
          />
        </ProfileBannerBgWrapper>

        <ProfileBannerInner>
          <AvatarWrapper
            size="xl"
            src={user?.image?.url ? user?.image?.url + `?now=${new Date().getTime()}` : null}
            text={user?.name}
            className="border-4 border-solid border-[#FDFCFA] shadow-dropshadow-sm"
          />

          <ProfileBannerHeader>
            <ProfileBannerData>
              <Badge
                variant="bright"
                size="sm"
              >
                {user?.['profile-names'] && user?.['profile-names'][0]}
              </Badge>

              {isActive ? (
                <Badge
                  variant="success"
                  size="sm"
                  className="ml-1"
                >
                  {t('journey.isActive')}
                </Badge>
              ) : (
                <Badge
                  variant="danger"
                  size="sm"
                  className="ml-1"
                >
                  {t('journey.isInactive')}
                </Badge>
              )}

              <ProfileBannerTitle>{user?.name}</ProfileBannerTitle>

              <ProfileBannerInfoWrapper>
                <ProfileBannerInfo>
                  <FiMail /> {user?.email}
                </ProfileBannerInfo>

                {userPhone != undefined ? (
                  <ProfileBannerInfo>
                    <FiPhone /> {userPhone}
                  </ProfileBannerInfo>
                ) : (
                  ''
                )}
              </ProfileBannerInfoWrapper>
            </ProfileBannerData>
          </ProfileBannerHeader>
        </ProfileBannerInner>
      </ProfileBanner>

      <TabNavBar
        selectedTab={selectedTab}
        abilities={abilities}
        id={id}
      />

      {selectedTab === 'metricas' && abilities?.metricas && (
        <UserMetricsView
          loading={loading}
          isStudent={isStudent}
          isAdmin={isAdmin}
          isCurrentUser={isCurrentUser}
          user={user}
          userMetrics={userMetrics}
          id={id}
        />
      )}

      {selectedTab === 'medalhas' ? <UserMedalsView medals={medals} /> : null}

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleShowUser')}
      />
    </DashboardContent>
  );
}
